import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const field = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]";
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";

export default function Settings() {
  const { user } = useAuth();
  const readOnly = user?.role !== "admin";
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  useEffect(() => { if (data) setF(data); }, [data]);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", f); toast.success("Settings saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (isLoading || !f) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF6500]" /></div>;

  return (
    <div className="mx-auto max-w-2xl animate-slide-up">
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Company Settings</h1>
      <p className="text-sm text-slate-500">Branding & delivery configuration</p>

      <div className="mt-5 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Company Name</label><input data-testid="settings-company-name" disabled={readOnly} value={f.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className={field} /></div>
          <div><label className={lbl}>ABN</label><input disabled={readOnly} value={f.abn || ""} onChange={(e) => set("abn", e.target.value)} className={field} /></div>
        </div>
        <div><label className={lbl}>Company Address</label><input disabled={readOnly} value={f.address || ""} onChange={(e) => set("address", e.target.value)} className={field} /></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Phone</label><input disabled={readOnly} value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Email</label><input disabled={readOnly} value={f.email || ""} onChange={(e) => set("email", e.target.value)} className={field} /></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Default GPS Verification Radius (m)</label><input data-testid="settings-gps-radius" type="number" disabled={readOnly} value={f.default_gps_radius || 100} onChange={(e) => set("default_gps_radius", parseInt(e.target.value || 100))} className={field} /></div>
          <div><label className={lbl}>POD Number Format</label><input disabled={readOnly} value={f.pod_format || ""} onChange={(e) => set("pod_format", e.target.value)} className={field} /></div>
        </div>
        {!readOnly && (
          <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="flex items-center gap-2 rounded-xl bg-[#0B192C] px-5 py-3 font-bold text-white tap-shrink disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Save Settings</>}
          </button>
        )}
        {readOnly && <p className="text-sm text-slate-400">Only admins can edit settings.</p>}
      </div>
    </div>
  );
}
